import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { ResponsiveImage } from '../../components/';

import css from './Logo.module.css';

const HEIGHT_16 = 16;
const HEIGHT_24 = 24;
const HEIGHT_36 = 36;
const HEIGHT_48 = 48;
const HEIGHT_OPTIONS = [HEIGHT_16, HEIGHT_24, HEIGHT_36, HEIGHT_48];

// logoSettings property supports only 3 types at this point
const isValidLogoSettings = settings =>
  settings?.format === 'image' && HEIGHT_OPTIONS.includes(settings?.height);
const isImageAsset = logo => logo?.type === 'imageAsset';

// Each type can have multiple image variants
const getVariantNames = variantsObj => {
  return Object.keys(variantsObj) || [];
};

// Variant data contains width and height among other things
// The width is needed for sizes attribute of responsive logo imgs
const getVariantData = variants => {
  // This assume that "scaled" variant exists
  // If other variants are introduced, this setup might need some tuning.
  return variants['scaled'];
};

// We have maximum heights for each logo type. It's enforced through classes
const getHeightClassName = height => {
  return height === HEIGHT_48 ? css.logo48 : height === HEIGHT_36 ? css.logo36 : height === HEIGHT_24 ? css.logo24 : css.logo16;
};

export const LogoComponent = props => {
  const {
    className,
    logoImageClassName,
    layout,
    marketplaceName,
    logoImageDesktop,
    logoImageMobile,
    logoSettings,
    subtitle,
    ...rest
  } = props;

  const logoClasses = className || css.root;
  const logoTextClasses = classNames(
    logoImageClassName || css.logoText,
    layout === 'mobile' ? css.logoTextMobile : css.logoTextDesktop
  );

  // Text Logo implementation - "neokasa" with favicon
  return (
    <div className={logoClasses}>
      <div className={css.logoContainer}>
        <img 
          src="/static/icons/favicon.png" 
          alt="Neokasa Logo" 
          className={css.faviconImage}
        />
        <div className={css.logoTextContainer}>
          <div
            className={logoTextClasses}
            data-logo="logo"
            aria-label="neokasa"
            {...rest}
          >
            {layout === 'mobile' ? (
              'Neokasa'
            ) : (
              <>
                Neokasa
              </>
            )}
          </div>
          {subtitle && <div className={css.subtitle}>{subtitle}</div>}
        </div>
      </div>
    </div>
  );
};

/**
 * This component returns a logo
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {('desktop' | 'mobile')} props.layout
 * @param {string?} props.alt alt text for logo image
 * @param {string?} props.subtitle subtitle text to display below the logo
 * @returns {JSX.Element} logo component
 */
const Logo = props => {
  const config = useConfiguration();
  const { layout = 'desktop', subtitle, ...rest } = props;
  // NOTE: logo images are set in hosted branding.json asset or src/config/brandingConfig.js
  const { logoImageDesktop, logoImageMobile, logoSettings, logoSubtitle } = config.branding;

  // Use provided subtitle or fall back to config default
  const displaySubtitle = subtitle || logoSubtitle;

  return (
    <LogoComponent
      {...rest}
      layout={layout}
      logoImageDesktop={logoImageDesktop}
      logoImageMobile={logoImageMobile}
      logoSettings={logoSettings}
      marketplaceName={config.marketplaceName}
      subtitle={displaySubtitle}
    />
  );
};

export default Logo;
